'use client';

import type { ClientTimedMediaMeta } from '../client-meta';
import { isWorkspaceTimedMediaFileAccepted } from '../timed-media-input-policy';
import { readClientTimedMediaMeta } from './timed-media-meta';
import { normalizeWorkspaceTimedMediaBrowserFile } from './upload-media-input-file';

export type TimedMediaPreflightConstraints = {
  allowedMimeTypes: readonly string[];
  maxCount?: number;
  maxSingleBytes?: number;
  maxTotalBytes?: number;
  minSingleDurationSeconds?: number;
  maxSingleDurationSeconds?: number;
  maxTotalDurationSeconds?: number;
  minVideoShortEdge?: number;
  maxVideoShortEdge?: number;
};

export type TimedMediaPreflightExistingItem = {
  sizeBytes: number;
  clientMeta: ClientTimedMediaMeta | null;
};

export type TimedMediaPreflightFailureReason =
  | 'invalid-type'
  | 'file-too-large'
  | 'total-size-too-large'
  | 'too-many'
  | 'duration-too-short'
  | 'duration-too-long'
  | 'total-duration-too-long'
  | 'resolution-too-low'
  | 'resolution-too-high'
  | 'metadata-unreadable';

export type TimedMediaPreflightErrorDetails = {
  kind: 'video' | 'audio';
  maxBytes?: number;
  maxCount?: number;
  minSeconds?: number;
  maxSeconds?: number;
  minShortEdge?: number;
  maxShortEdge?: number;
};

export class TimedMediaPreflightError extends Error {
  readonly reason: TimedMediaPreflightFailureReason;
  readonly details: TimedMediaPreflightErrorDetails;

  constructor(
    reason: TimedMediaPreflightFailureReason,
    details: TimedMediaPreflightErrorDetails
  ) {
    super(`Timed media preflight failed: ${reason}`);
    this.name = 'TimedMediaPreflightError';
    this.reason = reason;
    this.details = details;
  }
}

function validateStaticFile(params: {
  kind: 'video' | 'audio';
  file: File;
  constraints: TimedMediaPreflightConstraints;
}) {
  const { kind, file, constraints } = params;
  if (
    constraints.allowedMimeTypes.length > 0 &&
    !isWorkspaceTimedMediaFileAccepted({
      file,
      supportedMimeTypes: constraints.allowedMimeTypes,
    })
  ) {
    throw new TimedMediaPreflightError('invalid-type', { kind });
  }
  if (
    constraints.maxSingleBytes !== undefined &&
    file.size > constraints.maxSingleBytes
  ) {
    throw new TimedMediaPreflightError('file-too-large', {
      kind,
      maxBytes: constraints.maxSingleBytes,
    });
  }
}

function validateMeta(params: {
  kind: 'video' | 'audio';
  meta: ClientTimedMediaMeta;
  constraints: TimedMediaPreflightConstraints;
}) {
  const { kind, meta, constraints } = params;
  if (
    constraints.minSingleDurationSeconds !== undefined &&
    meta.durationSeconds < constraints.minSingleDurationSeconds
  ) {
    throw new TimedMediaPreflightError('duration-too-short', {
      kind,
      minSeconds: constraints.minSingleDurationSeconds,
    });
  }
  if (
    constraints.maxSingleDurationSeconds !== undefined &&
    meta.durationSeconds > constraints.maxSingleDurationSeconds
  ) {
    throw new TimedMediaPreflightError('duration-too-long', {
      kind,
      maxSeconds: constraints.maxSingleDurationSeconds,
    });
  }
  if (kind !== 'video') return;

  const width = meta.width ?? 0;
  const height = meta.height ?? 0;
  const shortEdge = Math.min(width, height);
  if (
    constraints.minVideoShortEdge !== undefined &&
    shortEdge < constraints.minVideoShortEdge
  ) {
    throw new TimedMediaPreflightError('resolution-too-low', {
      kind,
      minShortEdge: constraints.minVideoShortEdge,
    });
  }
  if (
    constraints.maxVideoShortEdge !== undefined &&
    shortEdge > constraints.maxVideoShortEdge
  ) {
    throw new TimedMediaPreflightError('resolution-too-high', {
      kind,
      maxShortEdge: constraints.maxVideoShortEdge,
    });
  }
}

export async function preflightTimedMediaFiles(params: {
  kind: 'video' | 'audio';
  files: readonly File[];
  existing: readonly TimedMediaPreflightExistingItem[];
  constraints: TimedMediaPreflightConstraints;
}) {
  const { constraints, kind } = params;
  if (
    constraints.maxCount !== undefined &&
    params.existing.length + params.files.length > constraints.maxCount
  ) {
    throw new TimedMediaPreflightError('too-many', {
      kind,
      maxCount: constraints.maxCount,
    });
  }

  for (const file of params.files) {
    validateStaticFile({ kind, file, constraints });
  }

  const totalBytes =
    params.existing.reduce((sum, item) => sum + item.sizeBytes, 0) +
    params.files.reduce((sum, file) => sum + file.size, 0);
  if (
    constraints.maxTotalBytes !== undefined &&
    totalBytes > constraints.maxTotalBytes
  ) {
    throw new TimedMediaPreflightError('total-size-too-large', {
      kind,
      maxBytes: constraints.maxTotalBytes,
    });
  }

  const checked = await Promise.all(
    params.files.map(async (file) => {
      const normalizedFile = normalizeWorkspaceTimedMediaBrowserFile(file);
      let clientMeta: ClientTimedMediaMeta;
      try {
        clientMeta = await readClientTimedMediaMeta(normalizedFile, kind);
      } catch {
        throw new TimedMediaPreflightError('metadata-unreadable', { kind });
      }
      validateMeta({ kind, meta: clientMeta, constraints });
      return { sourceFile: file, normalizedFile, clientMeta };
    })
  );

  const totalDuration =
    params.existing.reduce(
      (sum, item) => sum + (item.clientMeta?.durationSeconds ?? 0),
      0
    ) + checked.reduce((sum, item) => sum + item.clientMeta.durationSeconds, 0);
  if (
    constraints.maxTotalDurationSeconds !== undefined &&
    totalDuration > constraints.maxTotalDurationSeconds
  ) {
    throw new TimedMediaPreflightError('total-duration-too-long', {
      kind,
      maxSeconds: constraints.maxTotalDurationSeconds,
    });
  }

  return checked;
}
